import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { catalogService, CatalogItem } from '../../services/catalog.service';
import { internalTransferService, Wallet, InternalTransfer } from '../../services/internal-transfer.service';
import {
  internalDepositService,
  Deposit,
  CashClosing,
  LocationBalance,
} from '../../services/internal-deposit.service';

type InternalTab = 'transfers' | 'deposits' | 'closings';

export function InternalPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<InternalTab>('transfers');

  // Datos comunes
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [currencyTypes, setCurrencyTypes] = useState<CatalogItem[]>([]);
  const [locations, setLocations] = useState<CatalogItem[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canManageClosings =
    user?.role === 'sys_admin' ||
    user?.role === 'general_manager' ||
    user?.role === 'accountant';

  const canApproveClosings =
    user?.role === 'sys_admin' || user?.role === 'general_manager';

  useEffect(() => {
    loadInitial();
  }, []);

  const loadInitial = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [walletsData, currencies, locs] = await Promise.all([
        internalTransferService.getMyWallets(),
        catalogService.list('currency_types'),
        catalogService.list('financial_locations'),
      ]);
      setWallets(walletsData);
      setCurrencyTypes(currencies);
      setLocations(locs);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const reloadWallets = async () => {
    try {
      const data = await internalTransferService.getMyWallets();
      setWallets(data);
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p className="loading-text">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header-bar">
        <div>
          <h1>Interno</h1>
          <p className="subtitle">Transferencias, depósitos y cierre de caja</p>
        </div>
        <div className="actions">
          <button onClick={loadInitial} className="btn btn-ghost">🔄 Actualizar</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-bar">
        <button
          onClick={() => setActiveTab('transfers')}
          className={`tab-button ${activeTab === 'transfers' ? 'active' : ''}`}
        >
          📤 Transferencias
        </button>
        <button
          onClick={() => setActiveTab('deposits')}
          className={`tab-button ${activeTab === 'deposits' ? 'active' : ''}`}
        >
          💰 Depósitos
        </button>
        {canManageClosings && (
          <button
            onClick={() => setActiveTab('closings')}
            className={`tab-button ${activeTab === 'closings' ? 'active' : ''}`}
          >
            📊 Cierre de Caja
          </button>
        )}
      </div>

      {error && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="info-panel success">
          <span className="info-panel-icon">✅</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{success}</p>
          </div>
        </div>
      )}

      {/* Contenido por tab */}
      {activeTab === 'transfers' && (
        <TransfersTab
          wallets={wallets}
          onReload={reloadWallets}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}

      {activeTab === 'deposits' && (
        <DepositsTab
          wallets={wallets}
          currencyTypes={currencyTypes}
          locations={locations}
          onReload={reloadWallets}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}

      {activeTab === 'closings' && canManageClosings && (
        <ClosingsTab
          canApprove={canApproveClosings}
          currencyTypes={currencyTypes}
          locations={locations}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
    </div>
  );
}

// ============================================================
// TAB: TRANSFERENCIAS (extraído de la versión anterior)
// ============================================================
function TransfersTab({
  wallets,
  onReload,
  setError,
  setSuccess,
}: {
  wallets: Wallet[];
  onReload: () => Promise<void>;
  setError: (s: string) => void;
  setSuccess: (s: string) => void;
}) {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState<InternalTransfer[]>([]);
  const [fromWalletId, setFromWalletId] = useState('');
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [activeTransfer, setActiveTransfer] = useState<(InternalTransfer & { qr_url: string }) | null>(null);
  const [qrTokenInput, setQrTokenInput] = useState('');
  const [isReceiving, setIsReceiving] = useState(false);

  useEffect(() => {
    loadTransfers();
  }, []);

  useEffect(() => {
    if (!fromWalletId && wallets.length > 0) {
      const withBalance = wallets.find(w => w.balance > 0);
      setFromWalletId(withBalance?.id || wallets[0].id);
    }
  }, [wallets]);

  const loadTransfers = async () => {
    try {
      const data = await internalTransferService.list();
      setTransfers(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');
    setSuccess('');

    if (!fromWalletId || amount <= 0) {
      setError('Selecciona wallet y monto válido');
      setIsCreating(false);
      return;
    }

    const wallet = wallets.find(w => w.id === fromWalletId);
    if (!wallet || wallet.balance < amount) {
      setError('Saldo insuficiente');
      setIsCreating(false);
      return;
    }

    try {
      const transfer = await internalTransferService.create({
        from_wallet_id: fromWalletId,
        currency_type_id: wallet.currency_type_id,
        amount,
        notes: notes || undefined,
      });
      setActiveTransfer(transfer);
      setAmount(0);
      setNotes('');
      await loadTransfers();
    } catch (err: any) {
      setError(err.message || 'Error al crear');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!confirm('¿Cancelar?')) return;
    try {
      await internalTransferService.cancel(id);
      setActiveTransfer(null);
      await loadTransfers();
      setSuccess('Cancelada');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleReceive = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsReceiving(true);
    setError('');
    setSuccess('');

    try {
      const result = await internalTransferService.receive(qrTokenInput.trim());
      setSuccess(`Recibida. Nuevo saldo: ${formatCurrency(result.new_balance)}`);
      setQrTokenInput('');
      await loadTransfers();
      await onReload();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsReceiving(false);
    }
  };

  return (
    <div>
      {/* Mis Wallets */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>💼 Mis Wallets</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginTop: '12px' }}>
          {wallets.map(wallet => (
            <div
              key={wallet.id}
              onClick={() => setFromWalletId(wallet.id)}
              style={{
                padding: '16px', background: 'var(--bg)', borderRadius: 'var(--radius)',
                border: wallet.id === fromWalletId ? '2px solid var(--primary)' : '1px solid var(--light-gray)',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: '12px', color: 'var(--gray)' }}>{wallet.currency_name}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--primary)' }}>
                {formatCurrency(wallet.balance)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div className="card">
          <h3>📤 Enviar</h3>
          <form onSubmit={handleCreate} style={{ marginTop: '12px' }}>
            <div className="form-row">
              <div className="form-col">
                <label className="form-label required">Wallet origen</label>
                <select className="form-select" required value={fromWalletId} onChange={(e) => setFromWalletId(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {wallets.map(w => (
                    <option key={w.id} value={w.id}>{w.currency_name} — {formatCurrency(w.balance)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <label className="form-label required">Monto</label>
                <input type="number" className="form-input" required min="0.01" step="0.01"
                  value={amount || ''} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <label className="form-label">Notas</label>
                <textarea className="form-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
            <button type="submit" disabled={isCreating} className="btn btn-primary btn-block">
              {isCreating ? 'Generando...' : '📱 Generar QR'}
            </button>
          </form>
        </div>

        <div className="card">
          <h3>📥 Recibir</h3>
          <p style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '8px' }}>
            Ingresa el token del QR (modo prueba) o escanea con la PWA.
          </p>
          <form onSubmit={handleReceive} style={{ marginTop: '12px' }}>
            <div className="form-row">
              <div className="form-col">
                <label className="form-label required">Token del QR</label>
                <input type="text" className="form-input" required
                  value={qrTokenInput} onChange={(e) => setQrTokenInput(e.target.value)} />
              </div>
            </div>
            <button type="submit" disabled={isReceiving} className="btn btn-primary btn-block">
              {isReceiving ? 'Recibiendo...' : '✅ Recibir'}
            </button>
          </form>
        </div>
      </div>

      {/* QR activo */}
      {activeTransfer && (
        <div className="card" style={{ borderLeft: '4px solid var(--primary)', marginBottom: '20px' }}>
          <h3>📱 QR Generado</h3>
          <div style={{ display: 'flex', gap: '20px', marginTop: '16px', flexWrap: 'wrap' }}>
            <div style={{ padding: '12px', background: 'white', border: '1px solid var(--light-gray)', borderRadius: 'var(--radius)', display: 'inline-block' }}>
              <QRCodeSVG value={activeTransfer.qr_url} size={240} level="M" />
            </div>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <div className="info-row">
                <span className="info-row-label">Monto</span>
                <span className="info-row-value">{formatCurrency(activeTransfer.amount)}</span>
              </div>
              <div className="info-row">
                <span className="info-row-label">Expira</span>
                <span className="info-row-value">{new Date(activeTransfer.qr_expires_at).toLocaleString('es-MX')}</span>
              </div>
              <div className="info-row">
                <span className="info-row-label">Token</span>
                <span className="info-row-value" style={{ fontSize: '11px', wordBreak: 'break-all' }}>{activeTransfer.qr_token}</span>
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => { navigator.clipboard.writeText(activeTransfer.qr_token); setSuccess('Copiado'); }} className="btn btn-ghost">📋 Copiar</button>
                <button onClick={() => handleCancel(activeTransfer.id)} className="btn btn-danger">⛔ Cancelar</button>
                <button onClick={() => setActiveTransfer(null)} className="btn btn-ghost">Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Historial */}
      <div className="card">
        <h3>📜 Historial</h3>
        {transfers.length === 0 ? (
          <p style={{ color: 'var(--gray)' }}>Sin transferencias</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th><th>Dir.</th><th>Monto</th><th>Contraparte</th><th>Estado</th><th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {transfers.map(t => {
                  const isOut = t.created_by === user?.id;
                  const badges: Record<string, { class: string; label: string }> = {
                    pending: { class: 'badge-warning', label: '⏳ Pendiente' },
                    completed: { class: 'badge-success', label: '✅ Completada' },
                    cancelled: { class: 'badge-neutral', label: '⛔ Cancelada' },
                    expired: { class: 'badge-neutral', label: '⏰ Expirada' },
                  };
                  const b = badges[t.status] || { class: 'badge-neutral', label: t.status };
                  return (
                    <tr key={t.id}>
                      <td>{new Date(t.created_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                      <td><span className={`badge ${isOut ? 'badge-error' : 'badge-success'}`}>{isOut ? '📤' : '📥'}</span></td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(t.amount)}</td>
                      <td>{isOut ? (t.to_user_name || 'Pendiente') : (t.from_user_name || 'Desconocido')}</td>
                      <td><span className={`badge ${b.class}`}>{b.label}</span></td>
                      <td className="col-actions">
                        {isOut && t.status === 'pending' && (
                          <button onClick={() => handleCancel(t.id)} className="btn-action-reject">⛔</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// TAB: DEPÓSITOS
// ============================================================
function DepositsTab({
  wallets,
  currencyTypes,
  locations,
  onReload,
  setError,
  setSuccess,
}: {
  wallets: Wallet[];
  currencyTypes: CatalogItem[];
  locations: CatalogItem[];
  onReload: () => Promise<void>;
  setError: (s: string) => void;
  setSuccess: (s: string) => void;
}) {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [fromWalletId, setFromWalletId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadDeposits();
  }, []);

  useEffect(() => {
    if (!fromWalletId && wallets.length > 0) {
      const withBalance = wallets.find(w => w.balance > 0);
      setFromWalletId(withBalance?.id || wallets[0].id);
    }
  }, [wallets]);

  const loadDeposits = async () => {
    try {
      const data = await internalDepositService.listMyDeposits();
      setDeposits(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');
    setSuccess('');

    if (!fromWalletId || !toLocationId || amount <= 0) {
      setError('Completa todos los campos');
      setIsCreating(false);
      return;
    }

    const wallet = wallets.find(w => w.id === fromWalletId);
    if (!wallet) {
      setError('Wallet no encontrado');
      setIsCreating(false);
      return;
    }
    if (wallet.balance < amount) {
      setError(`Saldo insuficiente. Disponible: ${formatCurrency(wallet.balance)}`);
      setIsCreating(false);
      return;
    }

    try {
      await internalDepositService.createDeposit({
        from_wallet_id: fromWalletId,
        to_location_id: toLocationId,
        currency_type_id: wallet.currency_type_id,
        amount,
        notes: notes || undefined,
      });
      setSuccess('Depósito registrado');
      setAmount(0);
      setNotes('');
      await loadDeposits();
      await onReload();
    } catch (err: any) {
      setError(err.message || 'Error al depositar');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>💰 Registrar Depósito a Ubicación Física</h3>
        <p style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '8px' }}>
          Mueve efectivo o documentos de tu wallet personal a una ubicación física
          (cajón, caja fuerte, etc.)
        </p>
        <form onSubmit={handleSubmit} style={{ marginTop: '16px' }}>
          <div className="form-row cols-2">
            <div className="form-col">
              <label className="form-label required">Wallet origen</label>
              <select className="form-select" required value={fromWalletId} onChange={(e) => setFromWalletId(e.target.value)}>
                <option value="">Seleccionar...</option>
                {wallets.map(w => (
                  <option key={w.id} value={w.id}>{w.currency_name} — {formatCurrency(w.balance)}</option>
                ))}
              </select>
            </div>
            <div className="form-col">
              <label className="form-label required">Ubicación destino</label>
              <select className="form-select" required value={toLocationId} onChange={(e) => setToLocationId(e.target.value)}>
                <option value="">Seleccionar...</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name} ({l.location_type})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row cols-2">
            <div className="form-col">
              <label className="form-label required">Monto</label>
              <input type="number" className="form-input" required min="0.01" step="0.01"
                value={amount || ''} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
            </div>
            <div className="form-col">
              <label className="form-label">Notas</label>
              <input type="text" className="form-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <button type="submit" disabled={isCreating} className="btn btn-primary" style={{ marginTop: '12px' }}>
            {isCreating ? 'Registrando...' : '💰 Depositar'}
          </button>
        </form>
      </div>

      <div className="card">
        <h3>📜 Mis Depósitos</h3>
        {deposits.length === 0 ? (
          <p style={{ color: 'var(--gray)' }}>Sin depósitos registrados</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Fecha</th><th>Ubicación</th><th>Moneda</th><th style={{ textAlign: 'right' }}>Monto</th><th>Notas</th></tr>
              </thead>
              <tbody>
                {deposits.map(d => (
                  <tr key={d.id}>
                    <td>{new Date(d.deposit_date).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{d.location_name || '—'}</td>
                    <td>{d.currency_name || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(d.amount)}</td>
                    <td style={{ color: 'var(--gray)', fontSize: '13px' }}>{d.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// TAB: CIERRE DE CAJA
// ============================================================
function ClosingsTab({
  canApprove,
  currencyTypes,
  locations,
  setError,
  setSuccess,
}: {
  canApprove: boolean;
  currencyTypes: CatalogItem[];
  locations: CatalogItem[];
  setError: (s: string) => void;
  setSuccess: (s: string) => void;
}) {
  const [balances, setBalances] = useState<LocationBalance[]>([]);
  const [closings, setClosings] = useState<CashClosing[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showNewClosing, setShowNewClosing] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [currencyTypeId, setCurrencyTypeId] = useState('');
  const [closingDate, setClosingDate] = useState(new Date().toISOString().slice(0, 10));
  const [physicalCount, setPhysicalCount] = useState(0);
  const [justification, setJustification] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [bals, cls] = await Promise.all([
        internalDepositService.getLocationBalances(),
        internalDepositService.listClosings(),
      ]);
      setBalances(bals);
      setClosings(cls);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedBalance = () => {
    const b = balances.find(
      b => b.location_id === locationId && b.currency_type_id === currencyTypeId
    );
    return b?.balance ?? 0;
  };

  const difference = physicalCount - getSelectedBalance();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');

    if (!locationId || !currencyTypeId) {
      setError('Selecciona ubicación y moneda');
      setIsSaving(false);
      return;
    }

    if (Math.abs(difference) > 0.01 && !justification.trim()) {
      setError(`Hay una diferencia de ${formatCurrency(difference)}. Debes justificarla.`);
      setIsSaving(false);
      return;
    }

    try {
      await internalDepositService.createClosing({
        location_id: locationId,
        currency_type_id: currencyTypeId,
        closing_date: closingDate,
        physical_count: physicalCount,
        justification: justification || undefined,
      });
      setSuccess('Cierre registrado');
      setShowNewClosing(false);
      setLocationId('');
      setCurrencyTypeId('');
      setPhysicalCount(0);
      setJustification('');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al crear cierre');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdjust = async (id: string) => {
    if (!confirm('¿Ajustar el saldo del sistema a la cuenta física? Se creará un movimiento de ajuste.')) return;
    try {
      await internalDepositService.applyAdjustment(id);
      setSuccess('Ajuste aplicado');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleApprove = async (id: string) => {
    if (!confirm('¿Aprobar este cierre? No se podrá modificar después.')) return;
    try {
      await internalDepositService.approveClosing(id);
      setSuccess('Cierre aprobado');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (isLoading) {
    return <div className="loading-state"><div className="loading-spinner"></div></div>;
  }

  return (
    <div>
      {/* Saldos por ubicación */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="page-header-bar">
          <div>
            <h3>💼 Saldos por Ubicación</h3>
            <p className="subtitle">Saldo actual del sistema en cada ubicación física</p>
          </div>
          <button onClick={() => setShowNewClosing(true)} className="btn btn-primary">
            + Nuevo Cierre
          </button>
        </div>

        {balances.length === 0 ? (
          <p style={{ color: 'var(--gray)', marginTop: '12px' }}>Sin saldos registrados</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', marginTop: '12px' }}>
            {balances.map((b, i) => (
              <div key={i} style={{ padding: '16px', background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{b.location_name}</div>
                <div style={{ fontSize: '11px', color: 'var(--gray)' }}>{b.department_name || '—'} • {b.location_type}</div>
                <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '4px' }}>{b.currency_name || 'Sin moneda'}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--primary)', marginTop: '4px' }}>
                  {formatCurrency(b.balance ?? 0)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulario nuevo cierre */}
      {showNewClosing && (
        <div className="card" style={{ marginBottom: '20px', borderLeft: '4px solid var(--primary)' }}>
          <h3>📊 Nuevo Cierre de Caja</h3>
          <form onSubmit={handleCreate} style={{ marginTop: '16px' }}>
            <div className="form-row cols-2">
              <div className="form-col">
                <label className="form-label required">Ubicación</label>
                <select className="form-select" required value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-col">
                <label className="form-label required">Moneda</label>
                <select className="form-select" required value={currencyTypeId} onChange={(e) => setCurrencyTypeId(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {currencyTypes.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {locationId && currencyTypeId && (
              <div className="info-panel info" style={{ marginBottom: '12px' }}>
                <span className="info-panel-icon">ℹ️</span>
                <div className="info-panel-body">
                  <p className="info-panel-text">
                    Saldo del sistema: <strong>{formatCurrency(getSelectedBalance())}</strong>
                  </p>
                </div>
              </div>
            )}

            <div className="form-row cols-2">
              <div className="form-col">
                <label className="form-label required">Fecha del cierre</label>
                <input type="date" className="form-input" required value={closingDate} onChange={(e) => setClosingDate(e.target.value)} />
              </div>
              <div className="form-col">
                <label className="form-label required">Conteo físico</label>
                <input type="number" className="form-input" required min="0" step="0.01"
                  value={physicalCount || ''} onChange={(e) => setPhysicalCount(parseFloat(e.target.value) || 0)} />
              </div>
            </div>

            {Math.abs(difference) > 0.01 && (
              <div className={`info-panel ${difference < 0 ? 'error' : 'warning'}`} style={{ marginBottom: '12px' }}>
                <span className="info-panel-icon">⚠️</span>
                <div className="info-panel-body">
                  <p className="info-panel-title">
                    Diferencia: {formatCurrency(difference)}
                  </p>
                  <p className="info-panel-text">
                    {difference < 0 ? 'Faltante en caja' : 'Sobrante en caja'}. Justifica obligatoriamente.
                  </p>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-col">
                <label className={`form-label ${Math.abs(difference) > 0.01 ? 'required' : ''}`}>Justificación</label>
                <textarea className="form-textarea" rows={2} value={justification} onChange={(e) => setJustification(e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button type="submit" disabled={isSaving} className="btn btn-primary">
                {isSaving ? 'Guardando...' : 'Registrar Cierre'}
              </button>
              <button type="button" onClick={() => setShowNewClosing(false)} className="btn btn-ghost">Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* Historial */}
      <div className="card">
        <h3>📜 Historial de Cierres</h3>
        {closings.length === 0 ? (
          <p style={{ color: 'var(--gray)', marginTop: '12px' }}>Sin cierres registrados</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Folio</th><th>Fecha</th><th>Ubicación</th><th>Moneda</th>
                  <th style={{ textAlign: 'right' }}>Sistema</th>
                  <th style={{ textAlign: 'right' }}>Físico</th>
                  <th style={{ textAlign: 'right' }}>Diferencia</th>
                  <th>Estado</th>
                  <th className="col-actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {closings.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{c.closing_number}</td>
                    <td>{new Date(c.closing_date).toLocaleDateString('es-MX')}</td>
                    <td>{c.location_name}</td>
                    <td>{c.currency_name || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.system_balance)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.physical_count)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: Math.abs(c.difference) < 0.01 ? '#059669' : '#dc2626' }}>
                      {formatCurrency(c.difference)}
                    </td>
                    <td>
                      <span className={`badge ${
                        c.status === 'approved' ? 'badge-success' :
                        c.status === 'closed' ? 'badge-primary' : 'badge-warning'
                      }`}>
                        {c.status === 'approved' ? '✅ Aprobado' :
                         c.status === 'closed' ? '🔒 Cerrado' : '⏳ Abierto'}
                      </span>
                    </td>
                    <td className="col-actions">
                      <div className="action-buttons-group">
                        {c.status !== 'approved' && Math.abs(c.difference) > 0.01 && (
                          <button onClick={() => handleAdjust(c.id)} className="btn-action-approve" title="Ajustar saldo">
                            ⚖️ Ajustar
                          </button>
                        )}
                        {c.status !== 'approved' && canApprove && (
                          <button onClick={() => handleApprove(c.id)} className="btn-action-approve" title="Aprobar">
                            ✅ Aprobar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Helper
// ============================================================
function formatCurrency(amount: number) {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(amount || 0);
}